import { useState, useEffect, useCallback, useRef } from "react";

import { ConnectionAlert, FeatureFlag, Sidenav } from "../../components";
import { FEATURE_FLAGS } from "../../contexts";
import { useAuth, useToast } from "../../hooks";
import { dashboardSummaryService } from "../../services/DashboardSummaryService";
import { priorityDashboardService } from "../../services/PriorityDashboardService";
import { revenueMetricsService } from "../../services/RevenueMetricsService";
import { NOTIFICATION_TYPES } from "../../utils";
import { DashboardHeader } from "./DashboardHeader";
import { RevenueGoalCard } from "./RevenueGoalCard";
import { PortfolioHealth } from "./PortfolioHealth";
import { Pilots } from "./Pilots";
import { RevenuePipeline } from "./RevenuePipeline";
import { PriorityAccounts } from "./PriorityAccounts";

const REVENUE_UNIT_STORAGE_KEY = "dashboard_revenue_unit";
const REVENUE_UNITS = ["MRR", "ARR"];

const getStoredRevenueUnit = () => {
  const storedUnit = window.localStorage
    .getItem(REVENUE_UNIT_STORAGE_KEY)
    ?.toUpperCase();
  return REVENUE_UNITS.includes(storedUnit) ? storedUnit : "MRR";
};

export const DashboardV2Page = () => {
  const [healthExpanded, setHealthExpanded] = useState(true);
  const [pilotsExpanded, setPilotsExpanded] = useState(true);
  const [accountsFilter, setAccountsFilter] = useState("all");
  const [revenueUnit, setRevenueUnit] = useState(getStoredRevenueUnit);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [nrrProgress, setNrrProgress] = useState(null);
  const [priorityAccounts, setPriorityAccounts] = useState(null);
  const [priorityLoading, setPriorityLoading] = useState(true);
  const [priorityLoadingMore, setPriorityLoadingMore] = useState(false);
  const [priorityPage, setPriorityPage] = useState(1);
  const [priorityCards, setPriorityCards] = useState(null);
  // Bumped by every fresh load of the list (a tab switch or an owner-scope change). A response
  // that lands after a newer load began belongs to the old list and is dropped.
  const priorityLoadRef = useRef(0);
  // No `teamSettings` state: this page reads one preference out of the row and writes
  // that same one back. It used to hold the whole row so the save path could spread it,
  // which is the PAN-577 defect; with the spread gone nothing reads the row, and keeping
  // it would be a stale copy waiting to be reintroduced into a payload.
  const [revenueUnitLoading, setRevenueUnitLoading] = useState(true);
  const [revenueUnitSaving, setRevenueUnitSaving] = useState(false);
  const { apiClient, teamId } = useAuth();
  const showToast = useToast();

  useEffect(() => {
    let cancelled = false;

    const loadRevenueUnit = async () => {
      if (!teamId) {
        setRevenueUnitLoading(false);
        return;
      }

      setRevenueUnitLoading(true);
      try {
        const response = await apiClient.get(`/team-settings/${teamId}/`);
        const settings = response.data;
        const savedUnit = settings.dashboard_revenue_unit?.toUpperCase();

        if (!cancelled) {
          if (REVENUE_UNITS.includes(savedUnit)) {
            setRevenueUnit(savedUnit);
            window.localStorage.setItem(REVENUE_UNIT_STORAGE_KEY, savedUnit);
          }
        }
      } catch (error) {
        // The dashboard defaults to MRR if this optional preference cannot load.
        console.error("Error loading dashboard revenue unit:", error);
      } finally {
        if (!cancelled) setRevenueUnitLoading(false);
      }
    };

    loadRevenueUnit();
    return () => {
      cancelled = true;
    };
  }, [apiClient, teamId]);

  const handleRevenueUnitChange = useCallback(
    async (nextUnit) => {
      if (nextUnit === revenueUnit || revenueUnitSaving || !teamId) return;

      const previousUnit = revenueUnit;
      setRevenueUnit(nextUnit);
      window.localStorage.setItem(REVENUE_UNIT_STORAGE_KEY, nextUnit);
      setRevenueUnitSaving(true);

      try {
        // Send ONLY the field this toggle owns. This used to spread the whole settings
        // row and override `time_zone` with `row.time_zone || "UTC"` — which meant a
        // one-click MRR/ARR toggle wrote a fabricated "UTC" over the NULL that HubSpot
        // seeding deliberately leaves. The seed guard is `hs_id in {"", "default"}`, so
        // once a real portal id is seeded it never re-runs and the NULL is never
        // restored. Spreading the row also reverted any field another user or a sync
        // had changed since this page loaded. This toggle is not admin-gated, so it was
        // the most reachable way to hit both. PAN-577.
        //
        // PATCH, not PUT: the narrowed body omits `time_zone`, which is required on the
        // `fields = "__all__"` serializer, so the same body over PUT 400s.
        //
        // The row is no longer read to build the payload, so the refetch-on-failed-load
        // that used to seed the spread is gone with it. The displayed unit comes from
        // `revenueUnit`, already set optimistically above and rolled back in the catch.
        await apiClient.patch(`/team-settings/${teamId}/`, {
          dashboard_revenue_unit: nextUnit.toLowerCase(),
        });
      } catch (error) {
        setRevenueUnit(previousUnit);
        window.localStorage.setItem(REVENUE_UNIT_STORAGE_KEY, previousUnit);
        console.error("Error saving dashboard revenue unit:", error);
        showToast({
          content: "Failed to save dashboard revenue unit.",
          type: NOTIFICATION_TYPES.error,
        });
      } finally {
        setRevenueUnitSaving(false);
      }
    },
    // No `teamSettings` dependency: this callback no longer reads the row at all, so it
    // cannot capture a stale copy of it the way the spread did.
    [apiClient, revenueUnit, revenueUnitSaving, showToast, teamId],
  );

  const fetchSummary = useCallback(async () => {
    try {
      const data = await dashboardSummaryService.getSummary();
      setDashboardSummary(data);
    } catch {
      // Summary failure is non-critical — components show zero state
    }
  }, []);

  // The pipeline cards are the Priority Accounts tabs' own counts and MRR (PAN-818 spec §7).
  // The goal card keeps reading the dashboard summary.
  const fetchPriorityCards = useCallback(async () => {
    try {
      const data = await priorityDashboardService.getSummary();
      setPriorityCards(data?.cards ?? null);
    } catch {
      // Non-critical — the cards show a zero state
    }
  }, []);

  useEffect(() => {
    fetchPriorityCards();
  }, [fetchPriorityCards]);

  const fetchNrrProgress = useCallback(async () => {
    try {
      const data = await revenueMetricsService.getNrrGoalProgress();
      setNrrProgress(data);
    } catch {
      // Non-critical — the goal card falls back to a zero current state
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchNrrProgress();
  }, [fetchNrrProgress]);

  const fetchPriorityAccounts = useCallback(async (filterKey) => {
    const load = ++priorityLoadRef.current;
    setPriorityLoading(true);
    setPriorityPage(1);
    try {
      const params = filterKey !== "all" ? { category: filterKey } : {};
      const data = await priorityDashboardService.getAccounts(params);
      if (load !== priorityLoadRef.current) return;
      setPriorityAccounts(data);
    } catch {
      if (load !== priorityLoadRef.current) return;
      setPriorityAccounts({
        count: 0,
        next: null,
        previous: null,
        results: [],
      });
    } finally {
      if (load === priorityLoadRef.current) setPriorityLoading(false);
    }
  }, []);

  // "Show more" appends the next page of the same tab (the list used to stop at 15 rows).
  const loadMorePriorityAccounts = useCallback(async () => {
    const nextPage = priorityPage + 1;
    const load = priorityLoadRef.current;
    setPriorityLoadingMore(true);
    try {
      const params = { page: nextPage };
      if (accountsFilter !== "all") params.category = accountsFilter;
      const data = await priorityDashboardService.getAccounts(params);
      if (load !== priorityLoadRef.current) return;
      setPriorityAccounts((current) => ({
        ...data,
        results: [...(current?.results ?? []), ...(data?.results ?? [])],
      }));
      setPriorityPage(nextPage);
    } catch {
      // A failure for a list the user has already left is not worth reporting on the new one.
      if (load !== priorityLoadRef.current) return;
      showToast({
        content: "Failed to load more priority accounts.",
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setPriorityLoadingMore(false);
    }
  }, [accountsFilter, priorityPage, showToast]);

  useEffect(() => {
    const handleOwnerScopeChange = () => {
      fetchSummary();
      fetchNrrProgress();
      fetchPriorityCards();
      fetchPriorityAccounts(accountsFilter);
    };

    window.addEventListener("ownerScopeChanged", handleOwnerScopeChange);
    return () => {
      window.removeEventListener("ownerScopeChanged", handleOwnerScopeChange);
    };
  }, [
    fetchSummary,
    fetchNrrProgress,
    fetchPriorityCards,
    fetchPriorityAccounts,
    accountsFilter,
  ]);

  useEffect(() => {
    fetchPriorityAccounts(accountsFilter);
  }, [accountsFilter, fetchPriorityAccounts]);

  return (
    <div className="flex min-h-full flex-1 flex-row">
      <Sidenav />
      <div className="flex-1 overflow-y-auto">
        <div className="px-9 py-7">
          <ConnectionAlert />
          <DashboardHeader
            revenueUnit={revenueUnit}
            onRevenueUnitChange={handleRevenueUnitChange}
            disabled={revenueUnitLoading || revenueUnitSaving}
          />
          <RevenueGoalCard
            summary={dashboardSummary}
            nrrProgress={nrrProgress}
            revenueUnit={revenueUnit}
          />
          <PortfolioHealth
            expanded={healthExpanded}
            onToggle={() => setHealthExpanded(!healthExpanded)}
            revenueUnit={revenueUnit}
          />
          <FeatureFlag flag={FEATURE_FLAGS.PILOT_PIPELINE_SUPPORT}>
            <Pilots
              expanded={pilotsExpanded}
              onToggle={() => setPilotsExpanded(!pilotsExpanded)}
              revenueUnit={revenueUnit}
            />
          </FeatureFlag>
          <RevenuePipeline
            cards={priorityCards}
            onFilterChange={setAccountsFilter}
            revenueUnit={revenueUnit}
          />
          <PriorityAccounts
            accounts={priorityAccounts}
            loading={priorityLoading}
            filter={accountsFilter}
            onFilterChange={setAccountsFilter}
            onLoadMore={loadMorePriorityAccounts}
            loadingMore={priorityLoadingMore}
            revenueUnit={revenueUnit}
          />
        </div>
      </div>
    </div>
  );
};
